import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Grid, Typography, Box, Card, CardContent, Button,
  LinearProgress, Alert, IconButton, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { usePermissions } from '../../../utils/permissions';

const ResumePreview = ({ resumePath }) => {
  const [signedUrl, setSignedUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getSignedUrl = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('employee-documents')
          .createSignedUrl(resumePath, 3600);

        if (error) throw error;
        setSignedUrl(data.signedUrl);
      } catch (error) {
        console.error('Error getting signed URL:', error);
      } finally {
        setLoading(false);
      }
    };

    if (resumePath) {
      getSignedUrl();
    }
  }, [resumePath]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        alignItems="center" 
        justifyContent="center" 
        height="100%"
        gap={2}
      >
        <CircularProgress 
          size={40} 
          thickness={4}
          sx={{ 
            color: 'primary.main',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }} 
        />
        <Typography variant="body2" color="text.secondary">
          Loading preview...
        </Typography>
      </Box>
    );
  }

  if (!signedUrl) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="error">Unable to load preview</Typography>
      </Box>
    );
  }

  return (
    <iframe
      src={`${signedUrl}#toolbar=0`}
      width="100%"
      height="100%"
      style={{ border: 'none' }}
      title="Resume Preview"
    />
  );
};

export default function ResumeTab({ formData, onInputChange, companyId }) {
  const { canEditResume, canViewResume, canDeleteResume } = usePermissions();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');

    // Store file data for later upload on save
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      // Store file data in form for later upload
      onInputChange('resume_file_data', e.target.result);
      onInputChange('resume_name', file.name);
      onInputChange('resume_upload_date', new Date().toISOString());
      onInputChange('resume_file_size', file.size);
      onInputChange('resume_file_type', file.type);
      
      // Mark as pending upload
      onInputChange('resume_pending_upload', true);
    };
    
    fileReader.readAsArrayBuffer(file);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteResume = async () => {
    if (!formData.resume_path) return;
    
    if (!canDeleteResume()) {
      setError('You do not have permission to delete resume files');
      return;
    }
    
    setDeleteDialogOpen(true);
  };

  const confirmDeleteResume = async () => {
    try {
      // Mark for deletion on save - don't delete immediately
      onInputChange('resume_pending_delete', true);
      onInputChange('resume_path_to_delete', formData.resume_path);
      
      // Clear form data
      onInputChange('resume_path', '');
      onInputChange('resume_name', '');
      onInputChange('resume_upload_date', null);

    } catch (error) {
      console.error('Error marking resume for deletion:', error);
      setError('Error marking resume for deletion: ' + error.message);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleDownloadResume = async () => {
    if (!formData.resume_path) return;

    try {
      const { data, error } = await supabase.storage
        .from('employee-documents')
        .download(formData.resume_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = formData.resume_name || 'resume.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading resume:', error);
      setError('Error downloading file: ' + error.message);
    }
  };

  const handleViewResume = async () => {
    if (!formData.resume_path) return;

    try {
      const { data, error } = await supabase.storage
        .from('employee-documents')
        .createSignedUrl(formData.resume_path, 3600);

      if (error) throw error;

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error viewing resume:', error);
      setError('Error viewing file: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Check if user has any resume permissions
  if (!canViewResume() && !canEditResume()) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          You don't have permission to access resume information.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <DescriptionIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Resume Document
            </Typography>
          </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!formData.resume_path && !formData.resume_pending_upload ? (
              canEditResume() ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Upload the employee's resume. Supported formats: PDF, DOC, DOCX (Max 10MB)
                  </Typography>

                  <Box
                    sx={{
                      border: 2,
                      borderStyle: 'dashed',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: 'primary.50',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'primary.100'
                      }
                    }}
                    onClick={handleFileSelect}
                  >
                    <CloudUploadIcon 
                      sx={{ 
                        fontSize: 48, 
                        color: 'primary.main', 
                        mb: 2 
                      }} 
                    />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                      Upload Resume
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to browse or drag and drop your file here
                    </Typography>
                  </Box>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No resume uploaded yet.
                  </Typography>
                </Box>
              )
            ) : (
              <Box>
                {formData.resume_pending_upload ? (
                  <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    Resume ready for upload - changes will be saved when you click "Save Changes"
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Resume uploaded successfully
                  </Typography>
                )}

                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="between">
                    <Box display="flex" alignItems="center" gap={2}>
                      <DescriptionIcon color="primary" sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formData.resume_name}
                          {formData.resume_pending_upload && (
                            <Chip 
                              label="PENDING" 
                              size="small" 
                              color="warning" 
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Chip 
                            label={formData.resume_name?.split('.').pop()?.toUpperCase() || 'FILE'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                          {formData.resume_upload_date && (
                            <Typography variant="caption" color="text.secondary">
                              {formData.resume_pending_upload ? 'Will be uploaded' : 'Uploaded'} {formatDate(formData.resume_upload_date)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Box display="flex" gap={1}>
                      {!formData.resume_pending_upload && (
                        <>
                          <IconButton
                            onClick={handleViewResume}
                            title="View Resume"
                            size="small"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            onClick={handleDownloadResume}
                            title="Download Resume"
                            size="small"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </>
                      )}
                      {canDeleteResume() && (
                        <IconButton
                          onClick={handleDeleteResume}
                          title="Delete Resume"
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Card>

                {canDeleteResume() && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={handleFileSelect}
                      disabled={uploading}
                    >
                      Replace Resume
                    </Button>
                  </Box>
                )}

                {canDeleteResume() && (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

      {/* Resume Preview Section */}
      {formData.resume_path && (
        <Card sx={{
          borderRadius: 3,
          border: `1px solid rgba(0, 0, 0, 0.08)`,
          background: 'white',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
                border: '1px solid rgba(25, 118, 210, 0.2)'
              }}>
                <DescriptionIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                Resume Preview
              </Typography>
            </Box>
              
              <Box
                sx={{
                  width: '100%',
                  height: '600px',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: 'grey.50'
                }}
              >
                {formData.resume_name?.toLowerCase().endsWith('.pdf') ? (
                  <ResumePreview resumePath={formData.resume_path} />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    flexDirection="column"
                    gap={2}
                  >
                    <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                    <Typography variant="body1" color="text.secondary">
                      Preview not available for this file type
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadResume}
                    >
                      Download to View
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        PaperProps={{
          sx: {
            position: 'fixed !important',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete Resume
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{formData.resume_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteResume}
            color="error"
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}